import { useCallback, useEffect, useReducer } from "react";

import {
  documentoVacio,
  type CVDocument,
  type Estudio,
  type Experiencia,
  type Idioma,
  type Otro,
} from "../../lib/document/model";
import { ordenarExperiencias } from "../../lib/document/ordenar";
import { escribirRuta, type Ruta } from "../../lib/document/paths";

/**
 * El estado del editor: UN documento y nada mas.
 *
 * El builder viejo guarda lo mismo en `form_data` y en `cv_sections_data`, dos
 * claves de localStorage que se copian la una en la otra con
 * `syncSectionsToFormData()`. Cuando las dos discrepan gana la que lea primero
 * la pantalla que toque, y eso es exactamente lo que producia "el contacto de
 * otra hoja de vida aparece tras guardar". Aqui hay una sola clave, es un
 * respaldo ante recarga y nunca una fuente de verdad: al abrir, el servidor
 * manda.
 */

const CLAVE = "almia:editor:documento";

export type Estado = {
  doc: CVDocument | null;
  /** Sube en cada cambio local. El autosave de F2 lo usa para saber si hay algo pendiente. */
  revision: number;
};

export type Accion =
  | { tipo: "cargado"; doc: CVDocument }
  | { tipo: "ids:adoptar"; guardado: CVDocument }
  | { tipo: "editar"; ruta: Ruta; valor: unknown }
  | { tipo: "experiencia:añadir" }
  | { tipo: "experiencia:eliminar"; indice: number }
  | { tipo: "experiencia:reordenar" }
  | { tipo: "estudio:añadir" }
  | { tipo: "estudio:eliminar"; indice: number }
  | { tipo: "idioma:añadir" }
  | { tipo: "idioma:eliminar"; indice: number }
  | { tipo: "otro:añadir" }
  | { tipo: "otro:eliminar"; indice: number };

/**
 * Id temporal para una fila que aun no existe en el servidor.
 *
 * Negativo a proposito: asi `toDirectus` distingue de un vistazo lo que hay que
 * crear de lo que hay que actualizar, sin llevar una lista aparte de "nuevos"
 * que se pueda desincronizar.
 */
let siguienteIdTemporal = -1;
const idTemporal = () => siguienteIdTemporal--;

const experienciaNueva = (): Experiencia => ({
  id: idTemporal(),
  company_name: "",
  position: "",
  sector: "",
  start_date: null,
  end_date: null,
  current_job: false,
  details: "",
  achievements: "",
});

const estudioNuevo = (): Estudio => ({
  id: idTemporal(),
  institution_name: "",
  title_name: "",
  start_year: "",
  year: "",
  is_current: false,
  format: "",
  country_name: "",
  formation_level: "",
});

const idiomaNuevo = (): Idioma => ({ id: idTemporal(), name: "", level: "" });
const otroNuevo = (): Otro => ({ id: idTemporal(), section_name: "", section_text: "" });

const quitar = <T>(lista: T[], indice: number): T[] =>
  lista.filter((_, i) => i !== indice);

/**
 * Da ids reales a las filas recien creadas, SIN tocar su contenido.
 *
 * Es imprescindible: una fila que conserva su id negativo despues de guardarse
 * se vuelve a mandar en `create` en el siguiente guardado, y aparece DUPLICADA
 * en la hoja de vida.
 *
 * No se adopta el documento del servidor entero porque el usuario sigue
 * escribiendo mientras la peticion viaja, y pisarlo le borraria lo tecleado --
 * que es exactamente el bug que el builder viejo documenta en su `onSuccess`.
 * Solo viajan los ids: las filas guardadas cuyo id no estaba antes se reparten,
 * en orden, entre las que tienen id negativo.
 */
const adoptarIds = <T extends { id: number; recordId?: number }>(
  actuales: T[],
  guardadas: T[],
): T[] => {
  const conocidos = new Set(actuales.filter((f) => f.id >= 0).map((f) => f.id));
  const nuevas = guardadas.filter((f) => !conocidos.has(f.id));
  let i = 0;

  return actuales.map((fila) => {
    if (fila.id >= 0) return fila;
    const asignada = nuevas[i++];
    return asignada ? { ...fila, id: asignada.id, recordId: asignada.recordId } : fila;
  });
};

export const reducer = (estado: Estado, accion: Accion): Estado => {
  // Cargar es lo unico que puede ocurrir sin documento: el resto de acciones
  // llegan desde la UI, que no existe hasta que hay uno.
  if (accion.tipo === "cargado") {
    return { doc: accion.doc, revision: 0 };
  }
  if (!estado.doc) return estado;

  const doc = estado.doc;
  const siguiente = (d: CVDocument): Estado => ({
    doc: d,
    revision: estado.revision + 1,
  });

  switch (accion.tipo) {
    /**
     * Tras guardar. NO sube la revision a proposito: adoptar ids no es un
     * cambio del usuario, y si contara como tal dispararia otro guardado, que
     * adoptaria otra vez, en bucle.
     */
    case "ids:adoptar":
      return {
        revision: estado.revision,
        doc: {
          ...doc,
          experience: adoptarIds(doc.experience, accion.guardado.experience),
          education: adoptarIds(doc.education, accion.guardado.education),
          languages: adoptarIds(doc.languages, accion.guardado.languages),
          others: adoptarIds(doc.others, accion.guardado.others),
          // Los ids M2O tambien nacen al primer guardado.
          meta: { ...doc.meta, ...accion.guardado.meta },
        },
      };

    case "editar":
      return siguiente(escribirRuta(doc, accion.ruta, accion.valor));

    // Las nuevas entran al principio: es donde el usuario espera verlas, y en
    // experiencia coincide con el orden cronologico (lo mas reciente arriba).
    case "experiencia:añadir":
      return siguiente({ ...doc, experience: [experienciaNueva(), ...doc.experience] });
    case "experiencia:eliminar":
      return siguiente({ ...doc, experience: quitar(doc.experience, accion.indice) });

    /**
     * Reordenar es una accion aparte, no algo que pase solo tras cada tecla.
     *
     * Si se ordenara en cada cambio, escribir la fecha de inicio movería la
     * tarjeta de sitio a media escritura -- el usuario teclea "2" de "2024" y
     * el bloque salta al final de la lista. Se ordena al soltar el campo.
     */
    case "experiencia:reordenar":
      return siguiente({ ...doc, experience: ordenarExperiencias(doc.experience) });

    case "estudio:añadir":
      return siguiente({ ...doc, education: [estudioNuevo(), ...doc.education] });
    case "estudio:eliminar":
      return siguiente({ ...doc, education: quitar(doc.education, accion.indice) });

    case "idioma:añadir":
      return siguiente({ ...doc, languages: [...doc.languages, idiomaNuevo()] });
    case "idioma:eliminar":
      return siguiente({ ...doc, languages: quitar(doc.languages, accion.indice) });

    case "otro:añadir":
      return siguiente({ ...doc, others: [...doc.others, otroNuevo()] });
    case "otro:eliminar":
      return siguiente({ ...doc, others: quitar(doc.others, accion.indice) });

    default:
      return estado;
  }
};

// --- respaldo ante recarga ---------------------------------------------------

const leerRespaldo = (cvId: number): CVDocument | null => {
  try {
    const crudo = sessionStorage.getItem(CLAVE);
    if (!crudo) return null;
    const guardado = JSON.parse(crudo) as { cvId: number; doc: CVDocument };
    // Si el respaldo es de OTRA hoja de vida se descarta. Es la comprobacion
    // que le faltaba al builder viejo y por la que se mezclaban dos CVs.
    return guardado?.cvId === cvId ? guardado.doc : null;
  } catch {
    return null;
  }
};

const escribirRespaldo = (cvId: number, doc: CVDocument): void => {
  try {
    sessionStorage.setItem(CLAVE, JSON.stringify({ cvId, doc }));
  } catch {
    // Cuota llena o almacenamiento bloqueado. Se pierde el respaldo, no el
    // documento: la fuente sigue siendo el servidor.
  }
};

export const limpiarRespaldo = (): void => {
  try {
    sessionStorage.removeItem(CLAVE);
  } catch {
    /* nada que hacer */
  }
};

export const useDocumento = (cvId: number) => {
  const [estado, despachar] = useReducer(reducer, { doc: null, revision: 0 });

  // El respaldo se escribe en cada cambio, nunca se lee al vuelo. Solo lo
  // consulta `recuperarRespaldo`, y unicamente si la carga del servidor falla.
  useEffect(() => {
    if (estado.doc && estado.revision > 0) escribirRespaldo(cvId, estado.doc);
  }, [cvId, estado.doc, estado.revision]);

  const editar = useCallback(
    (ruta: Ruta, valor: unknown) => despachar({ tipo: "editar", ruta, valor }),
    [],
  );

  const recuperarRespaldo = useCallback(() => leerRespaldo(cvId), [cvId]);

  return {
    doc: estado.doc,
    revision: estado.revision,
    despachar,
    editar,
    recuperarRespaldo,
    documentoVacio,
  };
};
